import { Edit, Printer, FileIcon, DownloadCloud, Badge as BadgeIcon } from 'lucide-react';
import { Button, Badge, Drawer } from '@/components/ui';

interface RequestDetailDrawerProps {
  selectedItem: any;
  onClose: () => void;
  getStatusVariant: (status: string) => any;
  getTypeVariant: (type: string) => any;
}

export function RequestDetailDrawer({ selectedItem, onClose, getStatusVariant, getTypeVariant }: RequestDetailDrawerProps) {
  return (
    <Drawer
      isOpen={!!selectedItem}
      onClose={onClose}
      title="Chi tiết đề xuất"
      size="md"
      footer={
        <div className="flex gap-3 w-full">
          <Button variant="outline" className="flex-1 text-slate-700 sm:text-sm text-xs border-slate-200 hover:bg-slate-50">
            <Edit className="w-4 h-4 mr-2" />
            Chỉnh sửa
          </Button>
          <Button variant="outline" className="flex-1 text-slate-700 sm:text-sm text-xs border-slate-200 hover:bg-slate-50">
            <Printer className="w-4 h-4 mr-2" />
            In
          </Button>
        </div>
      }
    >
      {selectedItem && (
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-slate-50 -mx-6 -mt-6 p-6 border-b border-slate-100">
            <Badge variant={getStatusVariant(selectedItem.status)} className="mb-3 whitespace-nowrap shadow-sm">{selectedItem.status}</Badge>
            <h4 className="text-xl font-semibold text-slate-900 mb-2 leading-tight">{selectedItem.title}</h4>
            <div className="flex items-center gap-3">
              <span className="sm:text-sm text-xs text-slate-500 font-medium">{selectedItem.code}</span>
              <Badge variant={getTypeVariant(selectedItem.type)} className="whitespace-nowrap">{selectedItem.type}</Badge>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-slate-200">
            <button className="pb-2 sm:text-sm text-xs text-emerald-600 font-medium border-b-2 border-emerald-600 flex-1 text-center transition-all">Thông tin chung</button>
            <button className="pb-2 sm:text-sm text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-50 flex-1 text-center transition-all">Lịch sử xử lý</button>
          </div>

          {/* Info List */}
          <div className="space-y-4">
            <div className="flex sm:text-sm text-xs items-center py-1 border-b border-slate-50"><span className="w-1/3 text-slate-500 font-normal">Khu vực</span><span className="w-2/3 text-slate-800 font-medium">{selectedItem.zone?.zone_name || selectedItem.project?.name}</span></div>
            <div className="flex sm:text-sm text-xs items-center py-1 border-b border-slate-50"><span className="w-1/3 text-slate-500 font-normal">Loại đề xuất</span><span className="w-2/3 text-slate-800 font-medium">{selectedItem.type}</span></div>
            <div className="flex sm:text-sm text-xs items-center py-1 border-b border-slate-50"><span className="w-1/3 text-slate-500 font-normal">Ngày đề xuất</span><span className="w-2/3 text-slate-800 font-medium">{new Date(selectedItem.createdAt).toLocaleString('vi-VN')}</span></div>
            
            <div className="flex sm:text-sm text-xs items-start py-2 border-b border-slate-50">
              <span className="w-1/3 text-slate-500 font-normal pt-1">Người đề xuất</span>
              <div className="w-2/3 flex items-center gap-2">
                <img src={selectedItem.requester?.avatar_url || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(selectedItem.requester?.full_name || 'A')} alt="" className="w-8 h-8 rounded-full border border-slate-100 shadow-sm" />
                <div className="flex flex-col">
                  <span className="text-slate-800 font-medium leading-tight">{selectedItem.requester?.full_name}</span>
                  <span className="text-[10px] text-slate-500">Nhân viên kỹ thuật</span>
                </div>
              </div>
            </div>

            <div className="flex sm:text-sm text-xs items-center py-2 border-b border-slate-50">
              <span className="w-1/3 text-slate-500 font-normal">Mức độ ưu tiên</span>
              <span className="w-2/3"><Badge variant="warning" className="whitespace-nowrap">Trung bình</Badge></span>
            </div>

            <div className="flex flex-col sm:text-sm text-xs mt-4 p-3 bg-slate-50 rounded-md">
              <span className="text-slate-500 mb-1.5 font-medium flex items-center gap-1.5">
                <BadgeIcon className="w-3.5 h-3.5" />
                Mục đích
              </span>
              <span className="text-slate-800 leading-relaxed italic">Tăng tiện ích và trải nghiệm cho cư dân</span>
            </div>

            <div className="flex flex-col sm:text-sm text-xs mt-4">
              <span className="text-slate-500 mb-2 font-medium">Nội dung đề xuất</span>
              <div className="p-3 bg-white border border-slate-100 rounded-md text-slate-800 leading-relaxed shadow-sm">
                Đề xuất lắp đặt thêm 6 ghế nghỉ bằng gỗ và 2 thùng rác tại khu vườn hoa gần quảng trường trung tâm.
              </div>
            </div>
          </div>

          {/* Attachments */}
          <div className="pt-6 border-t border-slate-100">
            <h5 className="sm:text-sm text-xs font-semibold text-slate-900 mb-3 uppercase tracking-wider">Đính kèm</h5>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-3 rounded-md bg-white border border-slate-100 shadow-sm hover:border-emerald-200 transition-colors cursor-pointer group">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                    <FileIcon className="w-5 h-5" />
                  </div>
                  <div className="flex flex-col">
                    <span className="sm:text-sm text-xs text-slate-800 font-medium">Hình_ảnh_khu_vực.jpg</span>
                    <span className="text-[10px] text-slate-500 uppercase">2.4 MB • Image</span>
                  </div>
                </div>
                <button className="text-slate-400 hover:text-emerald-600 p-2 hover:bg-emerald-50 rounded-full transition-all">
                  <DownloadCloud className="w-5 h-5" />
                </button>
              </div>
              
              <div className="flex items-center justify-between p-3 rounded-md bg-white border border-slate-100 shadow-sm hover:border-emerald-200 transition-colors cursor-pointer group">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md bg-rose-50 text-rose-600 flex items-center justify-center group-hover:bg-rose-100 transition-colors">
                    <FileIcon className="w-5 h-5" />
                  </div>
                  <div className="flex flex-col">
                    <span className="sm:text-sm text-xs text-slate-800 font-medium">De_xuat_ghe_nghi.pdf</span>
                    <span className="text-[10px] text-slate-500 uppercase">1.8 MB • Document</span>
                  </div>
                </div>
                <button className="text-slate-400 hover:text-emerald-600 p-2 hover:bg-emerald-50 rounded-full transition-all">
                  <DownloadCloud className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Conclusion Info */}
          <div className="pt-6 border-t border-slate-100 space-y-4">
            <div className="p-4 bg-emerald-50/50 rounded-md border border-emerald-100">
              <span className="text-emerald-800 sm:text-sm text-xs font-semibold mb-2 block uppercase tracking-wider">Kết luận phê duyệt</span>
              <span className="text-emerald-700 sm:text-sm text-xs leading-relaxed font-medium">Đã phê duyệt. Tiến hành thực hiện theo kế hoạch.</span>
            </div>
            
            <div className="flex sm:text-sm text-xs items-center py-2">
              <span className="w-1/3 text-slate-500 font-normal">Người duyệt</span>
              <div className="w-2/3 flex items-center gap-2">
                <img src="https://i.pravatar.cc/150?u=10" alt="" className="w-8 h-8 rounded-full border border-slate-100 shadow-sm" />
                <div className="flex flex-col">
                  <span className="text-slate-800 font-semibold">Nguyễn Văn An</span>
                  <span className="text-[10px] text-slate-500 font-medium uppercase">Quản trị viên</span>
                </div>
              </div>
            </div>
            <div className="flex sm:text-sm text-xs items-center">
              <span className="w-1/3 text-slate-500 font-normal">Ngày duyệt</span>
              <span className="w-2/3 text-slate-800 font-medium">19/05/2024 10:15</span>
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
}
